import { DeepPartial, createRecord } from '../record';
import { EReportTargetComparisonOperators, EReportTargetValueTypes } from '../reportConfiguration';

interface IAccountSummaryChartXAxis {
  source: 'rows' | 'cols';
  series: string[][];
  title?: IAccountSummaryChartTextField;
}

interface IAccountSummaryChartYAxis {
  series: string[][];
  title?: IAccountSummaryChartTextField;
  multipleAxis?: {
    enabled: boolean;
    series?: string[][];
  };
}

interface IAccountSummaryChartTextField {
  enabled: boolean;
  text: string;
}

export type AccountSummaryChartType = 'bar' | 'donut' | 'gauge' | 'horizontalBar' | 'line' | 'pie' | null;

export const AccountSummaryChartTypes = {
  Bar: 'bar' as AccountSummaryChartType,
  Donut: 'donut' as AccountSummaryChartType,
  Gauge: 'gauge' as AccountSummaryChartType,
  HorizontalBar: 'horizontalBar' as AccountSummaryChartType,
  Line: 'line' as AccountSummaryChartType,
  Pie: 'pie' as AccountSummaryChartType
} as const;

export type AccountSummaryTarget = {
  condition: EReportTargetComparisonOperators;
  value: {
    type: EReportTargetValueTypes;
    amounts: (string | null)[];
  };
  status: string;
  elseStatus: string;
};

export type AccountSummaryChartValueType = 'currency' | 'numeric' | 'percentage';

export type BarLabelPosition = 'top' | 'middle' | 'bottom' | 'left' | 'center' | 'right';

export type AccountSummaryChartStacking = 'none' | 'normal' | 'percent';
export interface IAccountSummaryChart {
  type: AccountSummaryChartType;
  xAxis: IAccountSummaryChartXAxis;
  yAxis: IAccountSummaryChartYAxis;
  stacking: 'none' | 'normal' | 'percent';
  targets: AccountSummaryTarget[];
  title: IAccountSummaryChartTextField;
  description: IAccountSummaryChartTextField;
  valueType: AccountSummaryChartValueType;
  // Controls for bar value labels (visibility and position)
  barLabels?: {
    enabled: boolean;
    position?: BarLabelPosition;
  };
  legend: {
    align: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'centerLeft' | 'centerRight';
    enabled: boolean;
  };
  categories: string[];
  categoryIds: string[];
  /**
   * Custom series colors, keyed by the category id produced by `generateIdForSeriesItem` — e.g.
   * `c1.2025.FY2025` for a column value or `r1.Expenses` for a row-group value.
   *
   * Position is resolved at render time via `resolveSeriesColorIndex`, which prefers an exact id
   * match and otherwise tolerates a renamed COA description, so a key that answers to nothing in
   * the current chart is simply skipped. Keys for series belonging to a chart configuration the
   * user is not currently viewing are retained on purpose: switching chart type or axis source and
   * switching back must not lose colors.
   */
  seriesColors?: {
    [categoryId: string]: {
      color: string;
    };
  };
  afterNSlices?: number;
  /**
   * Custom color for a gauge chart, configured from the Visualization sidebar. Meaningful only when
   * `type === 'gauge'`.
   *
   * Deliberately outside `seriesColors`: a gauge's coloured regions are not clickable series points,
   * so they are configured directly rather than matched by category id, and a gauge color is never
   * shared with the bar or pie rendering of the same data point.
   *
   * The arc from the minimum up to the reading, and the reading's legend swatch. Absent means "keep
   * the stock gauge colors".
   */
  gaugeValueColor?: string;
}

const accountSummaryChartRecord = createRecord<IAccountSummaryChart>({
  type: null,
  xAxis: {
    source: 'rows',
    series: [],
    title: {
      enabled: false,
      text: ''
    }
  },
  yAxis: {
    series: [],
    title: {
      enabled: false,
      text: ''
    },
    multipleAxis: {
      enabled: false,
      series: []
    }
  },
  stacking: 'none',
  targets: [],
  title: {
    enabled: false,
    text: ''
  },
  description: {
    enabled: false,
    text: ''
  },
  valueType: 'currency',
  barLabels: {
    enabled: false,
    position: 'middle'
  },
  legend: {
    align: 'centerRight',
    enabled: false
  },
  seriesColors: {},
  categories: [],
  categoryIds: [],
  afterNSlices: 0,
  // Listed with no default because `createRecord` keeps only the keys named here — an omitted key is
  // dropped from every chart it copies. Absent is the correct initial state: a gauge with no
  // configured color must render the stock ones.
  gaugeValueColor: undefined
});

export const createAccountSummaryChart = (values: DeepPartial<IAccountSummaryChart> = {}): IAccountSummaryChart => ({
  ...accountSummaryChartRecord(values)
});
